import {useState, useEffect, Fragment, useContext, useMemo, useRef} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FormProvider, useForm} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faTrash} from "@fortawesome/free-solid-svg-icons";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

// Contexts
import {useToast} from "../../contexts/toast.js";
import {AuthContext} from "../../contexts/auth.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Utils
import MapProvider, {
  formatAddressComponents,
  formatAddressForGeocode
} from "../../utils/google/maps.js";
import {isWeatherConditionDepends} from "../checksheet-builder/helpers.js";
import {exists} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import Location from "../../components/Location.js";
import {InputCheck, InputTextGroup} from "../../components/form/FormInputs.js";

// Styles
import {flex} from "../../style/components/mixins.js";
import {border, pad, radius} from "../../style/components/variables.js";
import {
  Heading,
  FormField,
  Form,
  Inline,
  FormGroup,
  Button,
  ButtonLoader,
  Text,
  Small,
  Label
} from "../../style/components/general.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

const maps = new MapProvider();

const normalize = value => (value == null ? "" : String(value).trim().toLowerCase());

const sameAddress = (a, b) => {
  if (!a || !b) return false;

  const lineMatch =
    normalize(a.line1) !== "" &&
    normalize(a.line1) === normalize(b.line1) &&
    normalize(a.city) === normalize(b.city) &&
    normalize(a.state) === normalize(b.state) &&
    normalize(a.zipCode) === normalize(b.zipCode);

  const coordsPresent = [a.lat, a.lon, b.lat, b.lon].every(
    value => value !== "" && value != null && !Number.isNaN(Number(value))
  );
  const coordMatch =
    coordsPresent &&
    Number(a.lat).toFixed(5) === Number(b.lat).toFixed(5) &&
    Number(a.lon).toFixed(5) === Number(b.lon).toFixed(5);

  return lineMatch || coordMatch;
};

const GEOCODED_FIELDS = ["line1", "city", "state", "zipCode"];

const addressLinesChanged = (address, original) =>
  !original || GEOCODED_FIELDS.some(key => normalize(address[key]) !== normalize(original[key]));

const toFormAddress = address => ({
  ...address,
  hasDetails: !!address.details && address.details.length > 0,
  details: address.details || [{}]
});

// Resolves with [] instead of hanging or throwing when Google returns no match, errors, or
// isn't loaded (MapProvider.geocode returns null without calling back).
const geocode = params =>
  new Promise(resolve => {
    Promise.resolve(maps.geocode(params, results => resolve(results || [])))
      .then(response => {
        if (response === null) resolve([]);
      })
      .catch(() => resolve([]));
  });

const DEFAULT_LOCATION = {
  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    zipCode: "",
    details: [{}],
    hasDetails: false,
    primary: false
  }
};

const schema = yup.object().shape({
  address: yup.lazy(value => {
    const {lat, lon, line1} = value || {};
    if ((!lat || lat === "") && (!lon || lon === "") && line1 !== "" && line1 !== undefined)
      return yup.object().shape({
        line1: yup.string().required("Line 1 is required"),
        line2: yup.string().nullable(),
        city: yup.string().required("City is required"),
        state: yup.string().required("State is required"),
        zipCode: yup.string().required("Zip Code is required"),
        lat: yup.number().nullable(),
        lon: yup.number().nullable(),
        hasDetails: yup.bool(),
        details: yup
          .mixed()
          .nullable()
          .when("hasDetails", {
            is: val => !!val,
            then: () =>
              yup.array().of(
                yup.object().shape({
                  key: yup.string(),
                  value: yup.string()
                })
              )
          })
      });

    if (
      (!line1 || line1 === "") &&
      lat !== "" &&
      lat !== undefined &&
      lon !== "" &&
      lon !== undefined
    )
      return yup.object().shape({
        line1: yup.string().nullable(),
        line2: yup.string().nullable(),
        city: yup.string().nullable(),
        state: yup.string().nullable(),
        zipCode: yup.string().nullable(),
        lat: yup.number().typeError("Provide valid latitude.").required("Latitude is required"),
        lon: yup.number().typeError("Provide valid longitude.").required("Longitude is required"),
        details: yup
          .mixed()
          .nullable()
          .when("hasDetails", {
            is: val => !!val,
            then: () =>
              yup.array().of(
                yup.object().shape({
                  key: yup.string(),
                  value: yup.string()
                })
              )
          })
      });

    return yup.object().shape({
      line1: yup.string().required("Line 1 is required"),
      line2: yup.string().nullable(),
      city: yup.string().required("City is required"),
      state: yup.string().required("State is required"),
      zipCode: yup.string().required("Zip Code is required"),
      lat: yup.number().typeError("Provide valid latitude.").required("Latitude is required"),
      lon: yup.number().typeError("Provide valid longitude.").required("Longitude is required"),
      details: yup
        .mixed()
        .nullable()
        .when("hasDetails", {
          is: val => !!val,
          then: () =>
            yup.array().of(
              yup.object().shape({
                key: yup.string(),
                value: yup.string()
              })
            )
        })
    });
  }),
  primary: yup.boolean()
});

const ModalLocation = ({visible, setVisible, loadMap, initialAddress}) => {
  const isMounted = useMountedState();
  const {roleCanAccessResource} = useContext(AuthContext);
  const {setFacility, facility} = useContext(FacilityNavContext);

  const {addToast} = useToast();

  const [facilityLocations, setFacilityLocations] = useState([]);
  const [targetId, setTargetId] = useState(initialAddress || null);
  const [manage, setManage] = useState(!!initialAddress);
  const [closeOnSave, setCloseOnSave] = useState(!!initialAddress);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const {api: apiFacilities} = useApi("facilities", {
    suppress: {success: true, error: false}
  });
  const {api: apiChecksheets, data: checksheets} = useApi("checksheets");
  const {api: apiAddresses} = useApi("addresses");

  const defaultAddress = useMemo(
    () => facility?.addresses?.find(({id}) => initialAddress && id === initialAddress),
    [facility, initialAddress]
  );

  const form = useForm({
    defaultValues: defaultAddress ? {address: toFormAddress(defaultAddress)} : DEFAULT_LOCATION,
    resolver: yupResolver(schema)
  });
  const {watch, setValue, reset, handleSubmit} = form;

  const location = watch("address");
  const isPrimary = watch("address.primary");
  const hasDetails = watch("address.hasDetails");

  const checksheetHasWeatherReferences = useMemo(() => {
    if (!checksheets) return false;
    for (let i = 0; i < checksheets.length; i++) {
      const checksheet = checksheets[i];
      const {byId} = checksheet?.builder || {};
      if (byId) {
        const elements = Object.values(byId);
        const conditions = elements
          .filter(({condition}) => !!condition?.list)
          .map(({condition}) => condition);
        const formulas = elements.filter(({formula}) => !!formula).map(({formula}) => formula);
        for (let j = 0; j < conditions.length; j++) {
          const {list} = conditions[j];
          if (list?.some(({depends}) => isWeatherConditionDepends(depends))) return true;
        }

        for (let j = 0; j < formulas.length; j++) {
          const formula = formulas[j];
          if (formula?.some(({value}) => isWeatherConditionDepends(value))) return true;
        }
      }
    }
    return false;
  }, [checksheets]);

  // Format facility addresses as locations
  useEffect(() => {
    if (facility?.addresses?.length > 0) setFacilityLocations(facility.addresses);
  }, [facility.addresses, setValue]);

  useEffect(() => {
    if (isMounted() && facility) apiChecksheets.callGet(null, {facilityId: facility.id, withBuilder: true});
  }, [isMounted, apiChecksheets, facility]);

  const isDuplicate = address =>
    facilityLocations.some(
      existing => String(existing.id) !== String(targetId) && sameAddress(existing, address)
    );

  const finishSave = data => {
    const {addresses, builder} = data.facility;
    setFacilityLocations(addresses);
    loadMap(addresses, builder);
    setTargetId(null);
    reset(DEFAULT_LOCATION);
    setManage(false);
    setFacility(data.facility);
    if (closeOnSave) setVisible(false);
    // Toast after the form modal closes so it is not covered until Back.
    setTimeout(() => addToast(data.message || "Facility successfully updated!", "success"), 0);
  };

  const closeForm = () => {
    setManage(false);
    setTargetId(null);
    setCloseOnSave(false);
    reset(DEFAULT_LOCATION);
  };

  const removeAddress = id => {
    const address = facility.addresses.find(a => a.id === id);
    if (address.primary && checksheetHasWeatherReferences) {
      addToast(
        "Cannot remove! Existing checksheet relies on primary address for weather-based conditionals or formulas.",
        "error"
      );
    } else {
      apiAddresses
        .callDelete(id, {
          data: {
            facility: facility.id
          }
        })
        .then(({status}) => {
          if (status === 200) {
            if (facilityLocations.length === 1) {
              setManage(true);
              setValue("address.primary", true);
            }

            const temp = facilityLocations.filter(a => a.id !== id);
            setFacilityLocations(temp);
            loadMap(temp, facility.builder);

            setFacility({
              ...facility,
              addresses: temp,
              primaryAddress: temp.find(a => a.primary) || null
            });
          }
        });
    }
  };

  // Returns the address with coordinates and address lines filled in, or null after toasting why not.
  const resolveLocation = async address => {
    const {lat, lon, line1} = address;

    if (!line1) {
      const results = await geocode({latLng: maps.getLatLng(lat, lon)});
      const match = results.find(({types}) => types[0] === "street_address");
      if (!match) {
        addToast(
          "Coordinates do not correspond to a known address. Please try new coordinates.",
          "error"
        );
        return null;
      }
      return {...address, ...formatAddressComponents(match.address_components)};
    }

    const original = facilityLocations.find(({id}) => String(id) === String(targetId));
    if (exists(lat) && exists(lon) && !addressLinesChanged(address, original)) return address;

    // Typed edits keep the old coordinates in the form, so geocode whenever the lines change.
    const [match] = await geocode({address: formatAddressForGeocode(address)});
    if (!match) {
      addToast("Could not find that address. Check it and try again.", "error");
      return null;
    }
    return {...address, lat: match.geometry.location.lat(), lon: match.geometry.location.lng()};
  };

  const handleSave = async () => {
    // The ref blocks a second submit fired before the disabled state renders.
    if (savingRef.current) return;
    savingRef.current = true;

    const address = {...location};
    delete address.hasDetails;
    if (!address.details?.length || !address.details[0].key) address.details = null;

    setSaving(true);
    try {
      const payload = await resolveLocation(address);
      if (!payload) return;

      if (isDuplicate(payload)) {
        addToast("This address has already been added.", "error");
        return;
      }

      const {status, data} = await apiFacilities.callPut(facility.id, {
        address: targetId
          ? {id: targetId, ...payload, primary: isPrimary}
          : {...payload, primary: facilityLocations?.length === 0 || isPrimary}
      });
      if (status === 200 && data) finishSave(data);
    } finally {
      savingRef.current = false;
      if (isMounted()) setSaving(false);
    }
  };

  const editAddress = address => {
    setManage(true);
    setTargetId(address.id);
    reset({address: toFormAddress(address)});
  };

  if (manage && roleCanAccessResource("facility_address", "create"))
    return (
      <Modal
        visible={manage}
        setVisible={open => (open ? setManage(true) : closeForm())}
        goBack={closeForm}
        hasBackButton>
        <FormProvider {...form}>
          <Form onSubmit={handleSubmit(handleSave)} noValidate>
            <FormGroup>
              <Location defaultForm="Address" form={form} />

              <FormField>
                <InputCheck testId="hasDetails" name="address.hasDetails">
                  Additional details?
                </InputCheck>
                {hasDetails && (
                  <InputTextGroup
                    testId="details"
                    name="address.details"
                    label="Details"
                    canSetKey
                  />
                )}
              </FormField>

              <FormField>
                <InputCheck
                  name="address.primary"
                  testId="address.primary"
                  hidden={facilityLocations?.length === 0}>
                  Set as Primary Address
                </InputCheck>
              </FormField>

              <Inline>
                <SaveButton
                  type="submit"
                  data-testid="address.submit"
                  disabled={saving}
                  loading={saving ? 1 : 0}>
                  {saving && <SaveLoader />}
                  Save
                </SaveButton>
                <Button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  style={{marginLeft: `${pad}px`}}>
                  Cancel
                </Button>
              </Inline>
            </FormGroup>
          </Form>
        </FormProvider>
      </Modal>
    );

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <ModalHeading>
        <Heading>Location(s)</Heading>
      </ModalHeading>
      {facilityLocations?.map(address => (
        <Address key={address.id} data-testid="edit.locations">
          <div>
            <Label data-testid="edit.location" bold>
              {address.primary && <span>Primary</span>}
            </Label>
            <Text>
              <span>{address.line1}</span>
              <br />
              {address.line2 && address.line2 !== "" && (
                <>
                  <span>{address.line2}</span>
                  <br />
                </>
              )}
              <span>
                {address.city} {address.state}
                {address.zipCode && `, ${address.zipCode}`}
              </span>
              <br />
              <Small>
                {address.details &&
                  address.details.map(
                    detail =>
                      Object.keys(detail).length > 0 && (
                        <Fragment key={detail.key}>
                          {`${detail.key}: ${detail.value}`}
                          <br />
                        </Fragment>
                      )
                  )}
              </Small>
            </Text>
          </div>
          <Options>
            {roleCanAccessResource("facility_address", "update") && (
              <Button
                type="button"
                onClick={() => editAddress(address)}
                data-testid="textGroup.edit">
                <FontAwesomeIcon icon={faEdit} />
              </Button>
            )}
            {roleCanAccessResource("facility_address", "delete") && (
              <Button
                type="button"
                onClick={() => removeAddress(address.id)}
                data-testid="textGroup.remove">
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            )}
          </Options>
        </Address>
      ))}

      {facilityLocations?.length === 0 && <Text>No facility locations have been added.</Text>}

      {roleCanAccessResource("facility_address", "create") && (
        <Button type="button" onClick={() => setManage(true)}>
          Add Location
        </Button>
      )}
    </Modal>
  );
};

ModalLocation.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  loadMap: PropTypes.func.isRequired,
  initialAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

// Style Overrides
const ModalHeading = styled(Inline)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${pad}px;
`;

const Address = styled.div`
  ${flex("row", "wrap", "space-between", "start")};
  position: relative;
  width: 100%;
  padding: ${pad}px;
  margin-bottom: ${pad}px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.secondary};
`;

const Options = styled(Inline)`
  align-self: end;
  gap: ${pad}px;
`;

// Address saves often finish in under 300ms, which is before the shared loader's delayed fade-in starts.
const SaveButton = styled(Button)`
  animation-delay: 0s;
`;

const SaveLoader = styled(ButtonLoader)`
  animation-delay: 0s;
`;

export default ModalLocation;
